import {
  Banknote,
  CreditCard,
  HandCoins,
  Landmark,
  type LucideIcon,
  Mail,
  Store,
  Truck,
} from 'lucide-react'

import { useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * Jednotná značka služby: stejná dlaždice pro banky, dopravce, platební brány a integrace.
 * Značky ukazují logo z /public/brands (když je k dispozici), jinak barvu a zkratku. Obecné způsoby mají ikonu z lucide.
 */
type Brand = { label: string; bg: string; fg: string; name: string; logo?: string; logoBg?: string; wide?: boolean }
type Generic = { icon: LucideIcon; name: string }

const brands: Record<string, Brand> = {
  fio: { label: 'Fio', bg: '#1d5fae', fg: '#ffffff', name: 'Fio banka', logo: '/brands/fio.png', wide: true },
  moneta: { label: 'M', bg: '#e4002b', fg: '#ffffff', name: 'Moneta', logo: '/brands/moneta.png' },
  raiffeisen: { label: 'R', bg: '#fee600', fg: '#111111', name: 'Raiffeisenbank', logo: '/brands/raiffeisen.png' },
  airbank: { label: 'air', bg: '#000000', fg: '#9fd24a', name: 'Air Bank', logo: '/brands/airbank.png' },
  erste: { label: 'ČS', bg: '#2870ed', fg: '#ffffff', name: 'Česká spořitelna', logo: '/brands/erste.png' },
  zasilkovna: { label: 'Z', bg: '#c8102e', fg: '#ffffff', name: 'Zásilkovna', logo: '/brands/zasilkovna.svg' },
  ppl: { label: 'PPL', bg: '#0e3a7b', fg: '#ffd500', name: 'PPL', logo: '/brands/ppl.png' },
  balikovna: { label: 'B', bg: '#ffcc00', fg: '#003d7d', name: 'Balíkovna', logo: '/brands/balikovna.png' },
  ceskaPosta: { label: 'ČP', bg: '#ffcc00', fg: '#003d7d', name: 'Česká pošta', logo: '/brands/ceskaPosta.svg', logoBg: '#00296b' },
  sumup: { label: 'S', bg: '#111111', fg: '#ffffff', name: 'SumUp', logo: '/brands/sumup.svg' },
  fakturoid: { label: 'F', bg: '#1f9d55', fg: '#ffffff', name: 'Fakturoid', logo: '/brands/fakturoid.svg' },
}

const generics: Record<string, Generic> = {
  personalPickup: { icon: Store, name: 'Osobní odběr' },
  payOnPickup: { icon: Store, name: 'Platba při převzetí' },
  courier: { icon: Truck, name: 'Kurýr' },
  bankTransfer: { icon: Landmark, name: 'Bankovní převod' },
  cardPayment: { icon: CreditCard, name: 'Platba kartou' },
  card: { icon: CreditCard, name: 'Kartou' },
  cash: { icon: Banknote, name: 'Hotově' },
  cashOnDelivery: { icon: HandCoins, name: 'Dobírka' },
  cod: { icon: HandCoins, name: 'Dobírka' },
  email: { icon: Mail, name: 'E-mail' },
}

const sizes = {
  sm: 'size-6 rounded-md text-[9px]',
  md: 'size-8 rounded-lg text-[11px]',
  lg: 'size-10 rounded-lg text-[13px]',
} as const

export const ServiceMark = ({
  service,
  size = 'md',
  className,
}: {
  service: string
  size?: keyof typeof sizes
  className?: string
}) => {
  const brand = brands[service]
  const generic = generics[service]
  const [logoFailed, setLogoFailed] = useState(false)
  const base = cn('inline-grid shrink-0 place-items-center font-semibold tracking-[-0.01em] ring-1 ring-inset ring-black/5', sizes[size], className)

  if (brand?.logo && !logoFailed) {
    return (
      <span
        className={cn(base, 'overflow-hidden bg-white', brand.wide || size === 'sm' ? 'p-0.5' : 'p-1')}
        style={brand.logoBg ? { backgroundColor: brand.logoBg } : undefined}
        role="img"
        aria-label={brand.name}
        title={brand.name}
      >
        <img src={brand.logo} alt="" className={'size-full object-contain'} onError={() => setLogoFailed(true)} />
      </span>
    )
  }

  if (brand) {
    return (
      <span className={base} style={{ backgroundColor: brand.bg, color: brand.fg }} role="img" aria-label={brand.name} title={brand.name}>
        {brand.label}
      </span>
    )
  }

  const Icon = generic?.icon ?? Truck
  return (
    <span className={cn(base, 'bg-muted text-muted-foreground')} role="img" aria-label={generic?.name ?? service} title={generic?.name ?? service}>
      <Icon className={size === 'sm' ? 'size-3.5' : size === 'md' ? 'size-4' : 'size-5'} strokeWidth={1.75} />
    </span>
  )
}

/** Kód banky (např. 2010) na značku služby. */
const bankCodes: Record<string, string> = {
  '2010': 'fio',
  '0600': 'moneta',
  '5500': 'raiffeisen',
  '0800': 'erste',
  '3030': 'airbank',
}

export const serviceForBankCode = (code?: string | null) => (code ? bankCodes[code.padStart(4, '0')] : undefined)

/** Název banky z API (např. "Air bank", "Fio banka") na značku služby. */
export const serviceForBankName = (name?: string | null) => {
  const n = (name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  if (n.includes('air')) return 'airbank'
  if (n.includes('fio')) return 'fio'
  if (n.includes('moneta')) return 'moneta'
  if (n.includes('raiffeisen') || /\brb\b/.test(n)) return 'raiffeisen'
  if (n.includes('sporitelna') || n.includes('erste') || n.includes('csas')) return 'erste'
  return undefined
}

/** Symbol (např. měny) ve stejné dlaždici jako obecné značky. */
export const SymbolMark = ({
  symbol,
  label,
  size = 'md',
  className,
}: {
  symbol: string
  label?: string
  size?: keyof typeof sizes
  className?: string
}) => (
  <span
    className={cn(
      'inline-grid shrink-0 place-items-center bg-muted font-semibold text-foreground ring-1 ring-inset ring-black/5',
      sizes[size],
      symbol.length > 2 ? '' : size === 'sm' ? 'text-[12px]' : size === 'md' ? 'text-[14px]' : 'text-[16px]',
      className,
    )}
    role="img"
    aria-label={label ?? symbol}
  >
    {symbol}
  </span>
)
